import React from "react";

import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";

import { nullValuesReplacer } from "../../services/utils/json";

const ValueString = styled("span")(() => ({
  color: "#009315",
}));

const ValueNumber = styled("span")(() => ({
  color: "#004cef",
}));

const ValueBoolean = styled("span")(() => ({
  color: "#a89500",
}));

const ValueNull = styled("span")(() => ({
  color: "#676767",
}));

export const JsonTreeView: React.FC<{ data: any; title: string; showNullValues: boolean }> = ({
  data,
  title,
  showNullValues,
}) => {
  const renderValue = (value: any): React.ReactNode => {
    if (value === null) return <ValueNull>null</ValueNull>;

    switch (typeof value) {
      case "string":
        return <ValueString>"{value}"</ValueString>;
      case "number":
        return <ValueNumber>{value}</ValueNumber>;
      case "boolean":
        return <ValueBoolean>{String(value)}</ValueBoolean>;
      default:
        return String(value);
    }
  };

  // Recursive function to generate TreeItems.
  const renderTree = (nodeData: any, nodeIdPrefix: string, label?: string): React.ReactNode => {
    if (typeof nodeData === "function") return null;

    if (nodeData === null && !showNullValues) return null; // Skip rendering null values.

    if (typeof nodeData !== "object" || nodeData === null) {
      return (
        <TreeItem
          key={nodeIdPrefix}
          itemId={nodeIdPrefix}
          label={
            <Typography variant="body2" component="span">
              {label}: {renderValue(nodeData)}
            </Typography>
          }
        />
      );
    }

    if (Array.isArray(nodeData)) {
      return (
        <TreeItem key={nodeIdPrefix} itemId={nodeIdPrefix} label={label || "Array"}>
          {nodeData.map((item, index) => renderTree(item, `${nodeIdPrefix}-${index}`, `[${index}]`))}
        </TreeItem>
      );
    }

    return (
      <TreeItem key={nodeIdPrefix} itemId={nodeIdPrefix} label={label || title}>
        {Object.entries(nodeData).map(([key, value]) => renderTree(value, `${nodeIdPrefix}-${key}`, key))}
      </TreeItem>
    );
  };

  return (
    <SimpleTreeView
      sx={{
        flexGrow: 1,
        overflowY: "auto",
        border: "1px solid #ccc",
        borderRadius: 2,
        padding: 1,
      }}
      defaultExpandedItems={["root"]}
    >
      {renderTree(data, "root", undefined)}
    </SimpleTreeView>
  );
};

export const RawJsonView: React.FC<{ data: any; showNullValues: boolean }> = ({ data, showNullValues }) => {
  return (
    <Box
      sx={{
        flexGrow: 1,
        overflowY: "auto",
        border: "1px solid #ccc",
        borderRadius: 2,
        padding: 1,
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
        overflowX: "auto",
      }}
      data-testid="json-viewer"
    >
      {JSON.stringify(data, nullValuesReplacer(showNullValues), 2)}
    </Box>
  );
};
